// Editor.tsx
import { Lock, LockOpen, TextFields } from "@mui/icons-material";
import { Box } from "@mui/material";
import { Mark, mergeAttributes, CommandProps } from '@tiptap/core';
import type { EditorOptions, Editor as TipTapEditor } from "@tiptap/core";
import { useCallback, useRef, useState, useEffect } from "react";
import {
  LinkBubbleMenu,
  RichTextEditor,
  TableBubbleMenu,
  insertImages,
  type RichTextEditorRef,
} from "mui-tiptap";

import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./UseExtensions";
import InsertContentPopup, { ExtensionProps } from './InsertContentPopup';

function fileListToImageFiles(fileList: FileList): File[] {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}

type EditorProps = {
  content: string;
  onContentChange: (content: string) => void;
};

const Editor: React.FC<EditorProps> = ({ content, onContentChange }) => {
  const extensions = useExtensions({
    placeholder: "Add your own content here...",
  });

  console.log(content);

  const rteRef = useRef<RichTextEditorRef>(null);
  const [isEditable, setIsEditable] = useState(true);
  const [showMenuBar, setShowMenuBar] = useState(true);

  const [showInsertPopup, setShowInsertPopup] = useState(false);
  const [insertPopupProps, setInsertPopupProps] = useState<{
    extension: string;
    props: ExtensionProps;
  } | null>(null);

  const [selectedNode, setSelectedNode] = useState<{
    pos: number;
    node: any;
  } | null>(null);

  const handleUpdateContent = useCallback(
    ({ editor }: { editor: TipTapEditor }) => {
      const htmlContent = editor.getHTML();
      onContentChange(htmlContent);
    },
    [onContentChange],
  );

  const handleNewImageFiles = useCallback(
    (files: File[], insertPosition?: number): void => {
      if (!rteRef.current?.editor) return;

      const attributesForImageFiles = files.map((file) => ({
        src: URL.createObjectURL(file),
        alt: file.name,
      }));

      insertImages({
        images: attributesForImageFiles,
        editor: rteRef.current.editor,
        position: insertPosition,
      });
    },
    [],
  );

  const handleShowInsertContentPopup = useCallback((event: CustomEvent) => {
    console.log(event);
    const { extension, props, pos, node } = event.detail;
    setInsertPopupProps({ extension, props });
    setSelectedNode({ pos, node });
    console.log(extension, props);
    setShowInsertPopup(true);
  }, []);

  const handleInsert = (extension: string, props: ExtensionProps) => {
    if (!rteRef.current?.editor) {
      return;
    }

    // We'll get a chain for either replacing or inserting.
    const editor = rteRef.current.editor.chain().focus();

    switch (extension) {
      case 'Count':
        if (selectedNode) {
          // Replace the selected node
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentCount',
              attrs: props,
            })
            .run();
        } else {
          // Insert new content
          editor
            .insertContent({
              type: 'reactComponentCount',
              attrs: props,
            })
            .run();
        }
        break;

      case 'Link':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentLink',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentLink',
              attrs: props,
            })
            .run();
        }
        break;

      // ... similarly for other known node types ...
      case 'AlbumLink':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentAlbumLink',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentAlbumLink',
              attrs: props,
            })
            .run();
        }
        break;

      case 'AlbumEmbed':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentAlbumEmbed',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentAlbumEmbed',
              attrs: props,
            })
            .run();
        }
        break;

      case 'MyImage':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentMyImage',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentMyImage',
              attrs: props,
            })
            .run();
        }
        break;

      case 'ImageList':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentImageList',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentImageList',
              attrs: props,
            })
            .run();
        }
        break;

      case 'RecordBrowser':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentRecordBrowser',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentRecordBrowser',
              attrs: props,
            })
            .run();
        }
        break;

      case 'RecordBrowserPopup':
        if (selectedNode) {
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentRecordBrowserPopup',
              attrs: props,
            })
            .run();
        } else {
          editor
            .insertContent({
              type: 'reactComponentRecordBrowserPopup',
              attrs: props,
            })
            .run();
        }
        break;

      case 'RawHtml':
        if (selectedNode) {
          // Replace the selected node
          editor
            .deleteRange({ from: selectedNode.pos, to: selectedNode.pos + selectedNode.node.nodeSize })
            .insertContentAt(selectedNode.pos, {
              type: 'reactComponentRawHtml',
              attrs: props,
            })
            .run();
        } else {
          // Insert new content
          editor
            .insertContent({
              type: 'reactComponentRawHtml',
              attrs: props,
            })
            .run();
        }
        break;

      default:
        console.warn(`Unknown extension type: ${extension}`);
        return; // Do nothing
    }

    // After insertion (or replacement), reset state
    setShowInsertPopup(false);
    setSelectedNode(null);
  };

  const insertContent = () => {
    setShowInsertPopup(true);
  };

  const handleCancelInsert = () => {
    setShowInsertPopup(false);
    setSelectedNode(null);
  };

  useEffect(() => {
    document.addEventListener(
      'showInsertContentPopup',
      handleShowInsertContentPopup as EventListener
    );
    return () => {
      document.removeEventListener(
        'showInsertContentPopup',
        handleShowInsertContentPopup as EventListener
      );
    };
  }, [handleShowInsertContentPopup]);

  const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
    useCallback(
      (view, event, _slice, _moved) => {
        if (!(event instanceof DragEvent) || !event.dataTransfer) {
          return false;
        }
        const imageFiles = fileListToImageFiles(event.dataTransfer.files);
        if (imageFiles.length > 0) {
          const insertPosition = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;
          handleNewImageFiles(imageFiles, insertPosition);
          event.preventDefault();
          return true;
        }
        return false;
      },
      [handleNewImageFiles],
    );

  const handlePaste: NonNullable<EditorOptions["editorProps"]["handlePaste"]> =
    useCallback(
      (_view, event, _slice) => {
        if (!event.clipboardData) {
          return false;
        }
        const pastedImageFiles = fileListToImageFiles(event.clipboardData.files);
        if (pastedImageFiles.length > 0) {
          handleNewImageFiles(pastedImageFiles);
          return true;
        }
        return false;
      },
      [handleNewImageFiles],
    );

  return (
    <>
      <Box
        sx={{
          "& .ProseMirror": {
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              scrollMarginTop: showMenuBar ? 50 : 0,
            },
          },
        }}
      >
        <RichTextEditor
          ref={rteRef}
          extensions={extensions}
          content={content}
          editable={isEditable}
          editorProps={{
            handleDrop: handleDrop,
            handlePaste: handlePaste,
          }}
          onUpdate={handleUpdateContent}
          renderControls={() => (
            <EditorMenuControls
              editor={rteRef.current?.editor}
              onInsert={handleInsert}
              onOpenInsertPopup={insertContent}
            />
          )}
          RichTextFieldProps={{
            variant: "outlined",
            MenuBarProps: {
              hide: !showMenuBar,
            },
          }}
        >
          {() => (
            <>
              <LinkBubbleMenu />
              <TableBubbleMenu />
            </>
          )}
        </RichTextEditor>
      </Box>

      {showInsertPopup && (
        <InsertContentPopup
          onInsert={handleInsert}
          onCancel={handleCancelInsert}
          initialExtension={insertPopupProps?.extension}
          initialProps={insertPopupProps?.props}
        />
      )}
    </>
  );
};

export default Editor;